import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Moon, Sparkles, Clock, CheckCircle, Droplets } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';

function EveningSkincareGuide() {
  const navigate = useNavigate();

  const steps = [
    { step: 1, title: "Oil Cleanse", desc: "Massage oil cleanser on dry skin to dissolve makeup, SPF, and sebum", time: "1-2 min", tip: "Really massage it in - this is where the magic happens" },
    { step: 2, title: "Water-Based Cleanse", desc: "Follow with a gentle water-based cleanser to remove residue", time: "60 sec", tip: "This removes sweat, dirt, and remaining impurities" },
    { step: 3, title: "Toner (Optional)", desc: "Apply hydrating toner to damp skin to prep for treatments", time: "30 sec", tip: "Skip if using very hydrating serums" },
    { step: 4, title: "Treatment/Serum", desc: "Apply active ingredients (retinol, vitamin C, etc.) to damp skin", time: "30 sec", tip: "Wait 1 minute before next step for absorption" },
    { step: 5, title: "Eye Cream", desc: "Gently pat eye cream around orbital bone with ring finger", time: "30 sec", tip: "Never tug - the eye area is delicate" },
    { step: 6, title: "Moisturizer", desc: "Apply moisturizer in upward motions, including neck", time: "1 min", tip: "Your moisturizer seals in all the goodness" },
    { step: 7, title: "Face Oil (Optional)", desc: "A few drops of facial oil on top for extra nourishment", time: "30 sec", tip: "Great for dry/mature skin, skip if oily" }
  ];

  const activeIngredients = [
    { name: "Retinol", benefit: "Reduces wrinkles, evens tone, boosts collagen", usage: "Start 2x/week, build up" },
    { name: "Vitamin C", benefit: "Brightens, protects from free radicals", usage: "Best in AM but can use PM" },
    { name: "Niacinamide", benefit: "Minimizes pores, balances oil, reduces redness", usage: "Can use AM and PM" },
    { name: "Hyaluronic Acid", benefit: "Deep hydration, plumps fine lines", usage: "Apply to damp skin" },
    { name: "AHAs (Glycolic, Lactic)", benefit: "Exfoliates, brightens, smooths texture", usage: "2-3x/week max" },
    { name: "Peptides", benefit: "Firms, supports collagen production", usage: "Can layer with most actives" }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <header className="bg-white shadow-sm border-b border-primary/10">
        <div className="container mx-auto px-6 py-6">
          <Button onClick={() => navigate(-1)} variant="ghost" className="mb-4 text-secondary-700 hover:text-primary">
            <ArrowLeft className="mr-2 w-5 h-5" /> Back
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-4xl">
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm font-semibold mb-4">
            Week 3 • Day 4 Guide
          </span>
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-secondary-900 mb-4">
            Evening Skincare Routine
          </h1>
          <p className="text-xl text-secondary-600">Your Nighttime Repair Ritual</p>
        </div>

        {/* Hero */}
        <section className="bg-gradient-to-r from-indigo-500 to-purple-500 rounded-2xl p-8 text-white mb-8">
          <div className="flex items-start gap-4">
            <Moon className="w-12 h-12 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-2xl mb-2">Why Evening Matters More</h3>
              <p className="text-indigo-100 text-lg">
                Your skin repairs itself at night. Growth hormone peaks during deep sleep, collagen production increases, and skin cell turnover accelerates. 
                Give it the tools it needs.
              </p>
            </div>
          </div>
        </section>

        {/* Double Cleanse Explanation */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4 flex items-center gap-2">
            <Droplets className="w-6 h-6 text-indigo-500" />
            Why Double Cleanse?
          </h2>
          <p className="text-secondary-700 mb-4">
            Oil dissolves oil. Your first cleanse (oil-based) removes makeup, sunscreen, and excess sebum that water-based cleansers can't fully dissolve. 
            The second cleanse purifies skin and prepares it for treatment products.
          </p>
          <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-200">
            <p className="text-secondary-800 flex items-start gap-2">
              <Sparkles className="w-5 h-5 text-indigo-500 mt-1 flex-shrink-0" />
              <span><strong>Pro tip:</strong> Even if you don't wear makeup, your skin accumulates SPF, pollution, and sebum throughout the day. Double cleansing ensures a truly clean canvas.</span>
            </p>
          </div>
        </section>

        {/* Step by Step */}
        <section className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-8 border-2 border-indigo-200 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6 flex items-center gap-2">
            <Clock className="w-6 h-6 text-indigo-500" />
            The Complete Routine (10 Minutes)
          </h2>
          <div className="space-y-4">
            {steps.map((item) => (
              <div key={item.step} className="bg-white rounded-xl p-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-indigo-500 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                    {item.step}
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-start mb-1">
                      <h3 className="font-bold text-secondary-900">{item.title}</h3>
                      <span className="text-xs bg-indigo-200 text-indigo-700 px-2 py-1 rounded-full">{item.time}</span>
                    </div>
                    <p className="text-secondary-600 text-sm mb-1">{item.desc}</p>
                    <p className="text-xs text-indigo-600 italic">{item.tip}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Active Ingredients */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6">Active Ingredients to Know</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {activeIngredients.map((ingredient, index) => (
              <div key={index} className="p-4 bg-indigo-50 rounded-xl">
                <h3 className="font-bold text-indigo-700 mb-1">{ingredient.name}</h3>
                <p className="text-sm text-secondary-700 mb-2">{ingredient.benefit}</p>
                <p className="text-xs text-secondary-500 bg-white rounded-lg px-2 py-1 inline-block">{ingredient.usage}</p>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

export default EveningSkincareGuide;
